import PBSError from './PBSError'
export { default } from './PBSError'

export type SentryErrorData = {
  errorString: string
  errorTitle: string
  extraParams: UnknownObject
  visible?: boolean
}

export type UnknownObject = { [key: string]: any }

export interface IError extends Error {
  status?: number
  statusCode?: number
  response?: string
}

export enum ErrorStatus {
  FATAL_ERROR = 'fatal',
  GEO_ERROR = 'geo',
  DRM_ERROR = 'drm',
  VIDEO_UNAVAILABLE_ERROR = 'videoUnavailable',
  HTTP_400_ERROR = 400,
  HTTP_401_ERROR = 401,
  HTTP_403_ERROR = 403,
  HTTP_404_ERROR = 404,
  HTTP_405_ERROR = 405,
  HTTP_408_ERROR = 408,
  HTTP_409_ERROR = 409,
  HTTP_100_ERROR = 100,
  HTTP_500_ERROR = 500,
  HTTP_501_ERROR = 501,
  HTTP_502_ERROR = 502,
  HTTP_503_ERROR = 503,
  HTTP_504_ERROR = 504,
  TVAULT_ERROR = 'tvault',
  CANCEL_DONATION_ERROR = 'cancelDonation',
  TVAULT_TIMEOUT_ERROR = 'tvaultTimeout',
  DEFAULT_ERROR = 'default',
}

export type ErrorBody = {
  errorStatus: ErrorStatus
  errorCode: string
  errorPath: string
  errorType: string
  errorButtons: Buttons[]
  errorMessage: ErrorMessage
}

export type ErrorMessage = {
  errorTitle: string
  errorText: string
  errorTextCode: string
  e: PBSError
}

const donationSDKTitle = 'We were unable to attempt your donation'

export enum ErrorTitle {
  DONATION_OAT_ERROR = donationSDKTitle,
  DONATION_CONSENT_ERROR = donationSDKTitle,
  TVAULT_CONSENT_PAYLOAD_ERROR = donationSDKTitle,
  TVAULT_ERROR = 'There was a problem with your donation',
  MVAULT_ERROR = 'We were unable to activate PBS Passport',
  DEFAULT_ERROR = 'An unexpected error occurred.',
  CANCEL_DONATION_ERROR = 'We encountered a problem while cancelling your donations',
}
const donationBillingErrorText = `Try again later or visit your local station’s website to make a donation and unlock Passport. 
 
Please contact us at help@pbs.org for help.`

const mvaultError = `There was an error enabling your Passport access.

Please visit help.pbs.org or  contact us at help @pbs.org so we can fix this.`

export enum ErrorText {
  DONATION_OAT_ERROR = donationBillingErrorText,
  TVAULT_CONSENT_PAYLOAD_ERROR = 'Please try again or contact us at help@pbs.org for help.',
  DONATION_CONSENT_ERROR = donationBillingErrorText,
  TVAULT_ERROR = 'Please contact us at help@pbs.org',
  CANCEL_DONATION_ERROR = 'Please contact us at help@pbs.org for assistance.',
  MVAULT_ERROR = mvaultError,
  DEFAULT_ERROR = '',
}

export enum ErrorCodePrefix {
  DONATION_OAT_ERROR = ' AT ',
  DONATION_CONSENT_ERROR = ' BC ',
  TVAULT_CONSENT_PAYLOAD_ERROR = ' TC ',
  TVAULT_ERROR = 'TS',
  MVAULT_ERROR = 'MV',
  DEFAULT_ERROR = '',
  CANCEL_DONATION_ERROR = ' DC ',
}

// export const ErrorText = (e: PBSError) => <DonationErrorDescription e={e}/>

export enum Buttons {
  GO_BACK = '0',
  TRY_AGAIN = '1',
  EXIT = '2',
  OK = '3',
}

export type ErrorType =
  | 'DONATION_OAT_ERROR'
  | 'DONATION_CONSENT_ERROR'
  | 'TVAULT_CONSENT_PAYLOAD_ERROR'
  | 'TVAULT_ERROR'
  | 'MVAULT_ERROR'
  | 'DEFAULT_ERROR'
  | 'CANCEL_DONATION_ERROR'

export enum ErrorCodeStringTypes {
  BAD_REQUEST = 'BadRequest',
  UNAUTHORIZED = 'UnAuthorized',
  SERVICE_ACCOUNT_NOT_FOUND = 'ServiceAccountNotFound',
  SERVER_ERROR = 'ServerError',
  SERVICE_UNAVAILABLE = 'ServiceUnavailable',
  REQUEST_TIMED_OUT = 'Request Timed Out',
  FORBIDDEN = 'Forbidden',
  INTERNAL_SERVER_ERROR = 'Internal Server Error',
  VALUE_ERROR = 'ValueError',
  PERMISSION_DENIED = 'permission_denied',
  METHOD_NOT_ALLOWED = 'method_not_allowed',
  TIME_OUT = 'timeout',
  ERROR = 'error',
  UNEXPECTED_ERROR = 'unexpected_error',
}

export enum HTTPErrors {
  HTTP_400 = 400,
  HTTP_401 = 401,
  HTTP_403 = 403,
  HTTP_404 = 404,
  HTTP_405 = 405,
  HTTP_408 = 408,
  HTTP_409 = 409,
  HTTP_500 = 500,
  HTTP_502 = 502,
  HTTP_503 = 503,
}

export enum ErrorCodeString {
  DONATION_OAT_ERROR_400 = ErrorCodeStringTypes.BAD_REQUEST,
  DONATION_OAT_ERROR_401 = ErrorCodeStringTypes.UNAUTHORIZED,
  DONATION_OAT_ERROR_404 = ErrorCodeStringTypes.SERVICE_ACCOUNT_NOT_FOUND,
  DONATION_OAT_ERROR_408 = ErrorCodeStringTypes.REQUEST_TIMED_OUT,
  DONATION_OAT_ERROR_500 = ErrorCodeStringTypes.SERVER_ERROR,
  DONATION_OAT_ERROR_503 = ErrorCodeStringTypes.SERVICE_UNAVAILABLE,
  TVAULT_CONSENT_PAYLOAD_ERROR_400 = ErrorCodeStringTypes.BAD_REQUEST,
  TVAULT_CONSENT_PAYLOAD_ERROR_401 = ErrorCodeStringTypes.UNAUTHORIZED,
  TVAULT_CONSENT_PAYLOAD_ERROR_403 = ErrorCodeStringTypes.FORBIDDEN,
  TVAULT_CONSENT_PAYLOAD_ERROR_408 = ErrorCodeStringTypes.REQUEST_TIMED_OUT,
  TVAULT_CONSENT_PAYLOAD_ERROR_500 = ErrorCodeStringTypes.INTERNAL_SERVER_ERROR,
  DONATION_CONSENT_ERROR_400 = ErrorCodeStringTypes.BAD_REQUEST,
  DONATION_CONSENT_ERROR_401 = ErrorCodeStringTypes.UNAUTHORIZED,
  DONATION_CONSENT_ERROR_403 = ErrorCodeStringTypes.FORBIDDEN,
  DONATION_CONSENT_ERROR_408 = ErrorCodeStringTypes.REQUEST_TIMED_OUT,
  DONATION_CONSENT_ERROR_500 = ErrorCodeStringTypes.INTERNAL_SERVER_ERROR,
  DONATION_CONSENT_ERROR_503 = ErrorCodeStringTypes.SERVICE_UNAVAILABLE,
  TVAULT_ERROR_400 = ErrorCodeStringTypes.VALUE_ERROR,
  TVAULT_ERROR_401 = ErrorCodeStringTypes.UNAUTHORIZED,
  TVAULT_ERROR_403 = ErrorCodeStringTypes.PERMISSION_DENIED,
  TVAULT_ERROR_405 = ErrorCodeStringTypes.METHOD_NOT_ALLOWED,
  TVAULT_ERROR_408 = ErrorCodeStringTypes.TIME_OUT,
  TVAULT_ERROR_409 = ErrorCodeStringTypes.ERROR,
  TVAULT_ERROR_500 = ErrorCodeStringTypes.UNEXPECTED_ERROR,
  TVAULT_ERROR_502 = ErrorCodeStringTypes.SERVER_ERROR,
  MVAULT_ERROR_400 = ErrorCodeStringTypes.BAD_REQUEST,
  MVAULT_ERROR_401 = ErrorCodeStringTypes.UNAUTHORIZED,
  MVAULT_ERROR_403 = ErrorCodeStringTypes.FORBIDDEN,
  MVAULT_ERROR_404 = ErrorCodeStringTypes.SERVICE_ACCOUNT_NOT_FOUND,
  MVAULT_ERROR_408 = ErrorCodeStringTypes.REQUEST_TIMED_OUT,
  MVAULT_ERROR_500 = ErrorCodeStringTypes.SERVER_ERROR,
  DEFAULT = ErrorCodeStringTypes.ERROR,
}
